/**
 * ArchivioPage — F-006 Phase 5 (IMPL-P5-006).
 *
 * Compose the archivio list view: search bar + filters + virtualized table +
 * empty/no-results states + infinite scroll sentinel. Auth is enforced by
 * `RequireAuth` and the navbar/footer come from `AppLayout`.
 *
 * QueryClientProvider is mounted here pointing at `archivioQueryClient`, a
 * module-level singleton (see `lib/query-client.ts`). The cache therefore
 * survives navigation between `/app/archivio` and `/app/archivio/:id` so
 * cached list rows can hydrate the detail page. There is no app-wide
 * QueryClient yet — scoping to the feature avoids accidental coupling.
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';
import { RequireAuth } from '@/features/auth/components/RequireAuth';

import { archivioQueryClient } from '../lib/query-client';
import { ArchivioProvider, useArchivio } from '../hooks/useArchivio';
import { ArchivioSearchBar } from '../components/ArchivioSearchBar';
import { FiltriPanel } from '../components/FiltriPanel';
import { ActiveFiltersBar } from '../components/ActiveFiltersBar';
import { PraticheTable } from '../components/PraticheTable';
import { LoadMoreSentinel } from '../components/LoadMoreSentinel';
import { EmptyArchivio } from '../components/EmptyArchivio';
import { NoResultsState } from '../components/NoResultsState';
import { AlertCircle, Loader2 } from 'lucide-react';

function ArchivioInner() {
  const { status, refetch } = useArchivio();

  if (status === 'empty') {
    return <EmptyArchivio />;
  }

  if (status === 'error') {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <AlertTitle>Errore durante il caricamento</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>Si è verificato un problema nel caricamento dell'archivio.</p>
          <Button type="button" variant="outline" size="sm" onClick={refetch}>
            Riprova
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <FiltriPanel className="lg:w-72 lg:shrink-0" />
      <div className="flex-1 space-y-4">
        <ArchivioSearchBar />
        <ActiveFiltersBar />

        {status === 'loading' ? (
          <div
            className="flex items-center justify-center rounded-md border bg-muted/20 py-12 text-sm text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            Caricamento archivio…
          </div>
        ) : status === 'no-results' ? (
          <NoResultsState />
        ) : (
          <>
            <PraticheTable />
            <LoadMoreSentinel />
          </>
        )}
      </div>
    </div>
  );
}

export function ArchivioPage() {
  return (
    <RequireAuth>
      <AppLayout>
        <QueryClientProvider client={archivioQueryClient}>
          <ArchivioProvider>
            <div className="space-y-4">
              <header>
                <h1 className="text-2xl font-semibold tracking-tight">Archivio pratiche</h1>
                <p className="text-sm text-muted-foreground">
                  Tutte le pratiche registrate, con ricerca e download dei documenti.
                </p>
              </header>
              <ArchivioInner />
            </div>
          </ArchivioProvider>
        </QueryClientProvider>
      </AppLayout>
    </RequireAuth>
  );
}
